import { Link } from 'react-router-dom'
import { ArrowRight, Check, Globe2, PhoneCall, ShieldCheck } from 'lucide-react'

import { ContactSalesForm } from '@/components/marketing/contact-sales-form'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { createMetadata, marketingSeoPages } from '@/lib/seo'

const contactReasons = [
  {
    icon: PhoneCall,
    title: 'Migration planning',
    description: 'Review data, timing, and handoff requirements before leaving a legacy tool.',
  },
  {
    icon: Globe2,
    title: 'Multi-site rollout',
    description: 'Launch chapters, schools, or campuses on one shared system.',
  },
  {
    icon: ShieldCheck,
    title: 'Approval & ops review',
    description: 'Align finance, payments, and team permissions before launch.',
  },
] as const

const supportSignals = [
  'Multiple locations or teams',
  'Migrating off existing fundraising software',
  'Custom onboarding needs',
  'Brand and embed requirements',
] as const

const metadata = createMetadata(marketingSeoPages.contactSales)

export default function ContactSalesPage() {
  return (
    <div className="space-y-20 pb-20 pt-10 sm:space-y-24">
      <SeoHead metadata={metadata} />
      <section className="marketing-shell">
        <div className="grid gap-8 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
          <div className="space-y-8 py-4 lg:pr-8">
            <div className="space-y-5">
              <div className="eyebrow">Contact</div>
              <div className="space-y-4">
                <h1 className="max-w-3xl text-balance text-[2.45rem] font-semibold leading-[1.1] sm:text-[3.35rem] sm:leading-[1.06]">
                  Need help migrating or rolling out?
                </h1>
                <p className="max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                  KindLumen is designed for self-serve setup. Talk to us if you need to evaluate a migration, multiple sites, or an approval workflow before launch.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/signup">
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="/pricing">View pricing</Link>
              </Button>
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Reach out if you have
              </div>
              <h2 className="max-w-lg text-3xl font-semibold leading-[1.18] text-white sm:text-[2.2rem]">
                More than a basic setup ahead.
              </h2>
            </div>

            <div className="mt-8 grid gap-4">
              {supportSignals.map((item) => (
                <div key={item} className="rounded-[24px] border border-white/10 bg-white/10 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                    <p className="text-sm leading-6 text-white/75">{item}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-[26px] border border-white/10 bg-white/10 px-5 py-5">
              <p className="text-sm font-semibold text-white">We aim to reply within one business day.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading eyebrow="How we help" title="Where contact is worth it" />

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {contactReasons.map((item) => (
            <Card key={item.title} className="h-full">
              <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                <item.icon className="h-5 w-5" />
              </div>
              <CardTitle className="mt-5 text-xl">{item.title}</CardTitle>
              <CardDescription className="mt-4">{item.description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-8 lg:p-10">
          <div className="space-y-4">
            <div className="eyebrow">Tell us what you need</div>
            <h2 className="max-w-2xl text-3xl font-semibold leading-[1.18] sm:text-[2.2rem]">
              Share your context.
            </h2>
          </div>

          <ContactSalesForm />
        </div>
      </section>
    </div>
  )
}

export const Component = ContactSalesPage
